import { RefObject, useCallback } from "react";

import {
  BulkActionItemID,
  BulkActionsResult,
  BulkActionsStep,
  BulkActionsVariant,
} from "components/BulkActions/types";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageWorkerPool } from "hooks/useAnalytics/pages/workerPool";
import useBulkActions from "components/BulkActions/useBulkActions";
import { BulkActionItemTriggerResult } from "components/BulkActions/helpers";
import useExecutionQueue from "components/BulkActions/useExecutionQueue";
import { getDefaultOnContinueWith } from "components/BulkActions/Drawer/ResultsStep/helpers";
import { BulkActionResultsStepContinueWith } from "components/BulkActions/Drawer/ResultsStep/types";
import BulkActions from "components/BulkActions";
import BulkActionsFloatingBar from "components/BulkActions/FloatingBar";
import BulkActionsFloatingBarHeader from "components/BulkActions/FloatingBar/Header";
import BulkActionsFloatingBarSteps from "components/BulkActions/FloatingBar/Steps";
import BulkActionsPatternsActionsList from "components/BulkActionsPatterns/ActionsList";
import BulkActionsDrawer from "components/BulkActions/Drawer";
import BulkActionsDrawerActionSteps from "components/BulkActions/Drawer/ActionSteps";
import BulkActionsDrawerActionStepsHeader from "components/BulkActions/Drawer/ActionSteps/Header";
import BulkActionsDrawerActionStepsInfoBanner from "components/BulkActions/Drawer/ActionSteps/InfoBanner";
import BulkActionsDrawerResultsStep from "components/BulkActions/Drawer/ResultsStep";
import BulkActionsDrawerResultsStepHeader from "components/BulkActions/Drawer/ResultsStep/Header";
import BulkActionsDrawerResultsStepBody from "components/BulkActions/Drawer/ResultsStep/Body";
import BulkActionsDrawerResultsStepBodyEmptyTab from "components/BulkActions/Drawer/ResultsStep/Body/EmptyTab";
import BulkActionsDrawerResultsStepFooter from "components/BulkActions/Drawer/ResultsStep/Footer";
import { useResultTabItems } from "components/BulkActions/useResultTabItems";

import useWorkerRunsBulkActions from "./useWorkerRunsBulkActions";
import {
  BulkItemRunWithPosition,
  BulkWorkerPoolQueuedRunsActions,
  WorkerPoolQueuedRunsBulkActionsAnalyticsPayload,
} from "./types";
import WorkerPoolQueuedRunsBulkActionsConfirmActionForm from "./ConfirmActionForm";
import WorkerPoolQueuedRunsBulkActionsSelectedItemsView from "./SelectedItemsView";
import WorkerPoolQueuedRunsBulkActionsConfirmationView from "./ConfirmationView";
import WorkerPoolQueuedRunsBulkActionsResultItem from "./ResultItem";

type WorkerPoolQueuedRunsBulkActionsProps = {
  virtualizedListContainerRef: RefObject<HTMLElement | null>;
  selectedSet: Set<BulkActionItemID>;
  runsMap: Map<BulkActionItemID, BulkItemRunWithPosition>;
  onBulkResetAll: () => void;
  onFinish: () => Promise<unknown>;
  onItemDismiss: (id: BulkActionItemID) => void;
  onBulkContinueWith: (continueWith: Set<BulkActionItemID>) => void;
  isPublicWorkerPool: boolean;
};

const WorkerPoolQueuedRunsBulkActions = ({
  virtualizedListContainerRef,
  selectedSet,
  runsMap,
  isPublicWorkerPool,
  onFinish,
  onBulkResetAll,
  onBulkContinueWith,
  onItemDismiss,
}: WorkerPoolQueuedRunsBulkActionsProps) => {
  const selectedItemsCount = selectedSet.size;

  const analyticsPage = isPublicWorkerPool
    ? AnalyticsPageWorkerPool.WorkerPoolPublicQueue
    : AnalyticsPageWorkerPool.WorkerPoolQueue;

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: analyticsPage,
  });

  const workerBulkActions = useWorkerRunsBulkActions(isPublicWorkerPool);

  const {
    selectedBulkAction,
    itemsPerAction,
    availableActions,
    resetSelectedAction,
    skippedSelectedEntityItems,
    selectedEntityItems,
    actionsPerItem,
    applicableSelectedEntityItems,
  } = useBulkActions({
    actions: workerBulkActions,
    selectedSet,
    entityItemsMap: runsMap,
    analyticsPage,
  });

  const onItemTrigger = useCallback(
    (id: BulkActionItemID): Promise<BulkActionItemTriggerResult> => {
      const run = runsMap.get(id);
      if (!run || !selectedBulkAction) {
        return Promise.reject();
      }
      if (!selectedBulkAction.condition(run).result) {
        return Promise.resolve(BulkActionsResult.Skipped);
      }
      return selectedBulkAction.mutation(run, undefined);
    },
    [runsMap, selectedBulkAction]
  );

  const {
    triggerExecution,
    bulkActionItemResults,
    stopExecution,
    stopExecutionForItem,
    bulkActionResultsMetadata,
    resetBulkActionsExecutionQueue,
  } = useExecutionQueue({
    onItemTrigger,
    onAllItemsFinished: onFinish,
    analyticsPage,
  });

  const { resultsTabItems, saveEntityMap, resetEntityMap } =
    useResultTabItems<BulkItemRunWithPosition>(bulkActionItemResults);

  const handleClose = useCallback(() => {
    onBulkResetAll();
    resetSelectedAction();
    resetBulkActionsExecutionQueue();
    resetEntityMap();
    stopExecution();
  }, [
    onBulkResetAll,
    resetBulkActionsExecutionQueue,
    resetEntityMap,
    resetSelectedAction,
    stopExecution,
  ]);

  const handleOnConfirm = useCallback(
    (analyticsPayload: WorkerPoolQueuedRunsBulkActionsAnalyticsPayload) => {
      if (selectedBulkAction) {
        const items = itemsPerAction.get(selectedBulkAction.key) || [];
        const skippedItems = skippedSelectedEntityItems.map((item) => item.id);

        triggerExecution(items, undefined, skippedItems);
        saveEntityMap(runsMap);

        trackSegmentAnalyticsEvent("Bulk Actions - Action Confirmed", {
          applicableCount: items?.length || 0,
          notApplicableCount: skippedItems.length,
          action: selectedBulkAction.key,
          ...analyticsPayload,
        });
      }
    },
    [
      itemsPerAction,
      saveEntityMap,
      selectedBulkAction,
      skippedSelectedEntityItems,
      trackSegmentAnalyticsEvent,
      triggerExecution,
      runsMap,
    ]
  );

  const handleOnCancel = (variant: BulkActionsVariant) => () => {
    if (selectedBulkAction) {
      resetSelectedAction();
      const items = itemsPerAction.get(selectedBulkAction.key);
      trackSegmentAnalyticsEvent("Bulk actions - Action Cancelled", {
        applicableCount: items?.length || 0,
        notApplicableCount: skippedSelectedEntityItems.length,
        action: selectedBulkAction.key,
        view: variant,
      });
    }
  };

  const handleContinueWith = useCallback(
    (continueWith: BulkActionResultsStepContinueWith) => {
      getDefaultOnContinueWith(resultsTabItems, onBulkContinueWith)(continueWith);
      resetSelectedAction();
      resetBulkActionsExecutionQueue();
    },
    [onBulkContinueWith, resetSelectedAction, resultsTabItems, resetBulkActionsExecutionQueue]
  );

  return (
    <BulkActions
      selectedItemsCount={selectedItemsCount}
      onContinueWith={handleContinueWith}
      onClose={handleClose}
      isExecutingBulkActions={false}
    >
      <BulkActionsFloatingBar virtualizedListContainerRef={virtualizedListContainerRef}>
        <BulkActionsFloatingBarHeader
          selectedItemsCount={selectedItemsCount}
          applicableItemsCount={
            selectedBulkAction?.key ? itemsPerAction.get(selectedBulkAction.key)?.length : 0
          }
          analyticsPage={analyticsPage}
        />
        <BulkActionsFloatingBarSteps>
          {!selectedBulkAction && (
            <BulkActionsPatternsActionsList
              variant={BulkActionsVariant.FloatingBar}
              availableActions={availableActions}
              onEmptyActionsCancel={handleClose}
            />
          )}

          {selectedBulkAction && (
            <WorkerPoolQueuedRunsBulkActionsConfirmActionForm
              variant={BulkActionsVariant.FloatingBar}
              action={selectedBulkAction}
              onConfirm={handleOnConfirm}
              onCancel={handleOnCancel(BulkActionsVariant.FloatingBar)}
            />
          )}
        </BulkActionsFloatingBarSteps>
      </BulkActionsFloatingBar>

      <BulkActionsDrawer>
        <BulkActionsDrawerActionSteps>
          <BulkActionsDrawerActionStepsHeader
            analyticsPage={analyticsPage}
            step={
              !selectedBulkAction
                ? BulkActionsStep.ChooseAction
                : BulkActionsStep.ActionConfirmation
            }
          />
          <BulkActionsDrawerActionStepsInfoBanner />

          {!selectedBulkAction && (
            <WorkerPoolQueuedRunsBulkActionsSelectedItemsView
              analyticsPage={analyticsPage}
              items={selectedEntityItems}
              onItemDismiss={onItemDismiss}
              availableActions={availableActions}
              actionsPerItem={actionsPerItem}
              onEmptyActionsCancel={handleClose}
            />
          )}

          {selectedBulkAction && (
            <WorkerPoolQueuedRunsBulkActionsConfirmationView
              analyticsPage={analyticsPage}
              applicableItems={applicableSelectedEntityItems}
              skippedItems={skippedSelectedEntityItems}
              onItemDismiss={onItemDismiss}
              selectedBulkAction={selectedBulkAction}
              onConfirm={handleOnConfirm}
              onCancel={handleOnCancel(BulkActionsVariant.Drawer)}
            />
          )}
        </BulkActionsDrawerActionSteps>

        <BulkActionsDrawerResultsStep>
          {({ currentTab, setCurrentTab }) => (
            <>
              <BulkActionsDrawerResultsStepHeader
                title={selectedBulkAction?.resultTitle || "Results"}
                analyticsPage={analyticsPage}
              />

              <BulkActionsDrawerResultsStepBody
                currentTab={currentTab}
                setCurrentTab={setCurrentTab}
                counter={resultsTabItems}
              >
                {() =>
                  resultsTabItems[currentTab].count === 0 ? (
                    <BulkActionsDrawerResultsStepBodyEmptyTab currentTab={currentTab} />
                  ) : (
                    resultsTabItems[currentTab].entitiesList.map(({ status, items }) =>
                      items.map((item) => (
                        <WorkerPoolQueuedRunsBulkActionsResultItem
                          key={item.id}
                          item={item}
                          tab={currentTab}
                          resultsMetadata={bulkActionResultsMetadata}
                          stopExecution={() => stopExecutionForItem(item.id)}
                          status={status}
                        />
                      ))
                    )
                  )
                }
              </BulkActionsDrawerResultsStepBody>

              <BulkActionsDrawerResultsStepFooter
                currentTab={currentTab}
                bulkActionItemResults={bulkActionItemResults}
                onContinueEnabled
                stopExecution={stopExecution}
                analyticsPage={analyticsPage}
                rerunWithFailedOnly={
                  selectedBulkAction?.key === BulkWorkerPoolQueuedRunsActions.DISCARD
                }
              />
            </>
          )}
        </BulkActionsDrawerResultsStep>
      </BulkActionsDrawer>
    </BulkActions>
  );
};

export default WorkerPoolQueuedRunsBulkActions;
